import { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { ArrowLeft, Globe, Zap, CheckCircle2, Link as LinkIcon, Loader2, Lock, Crown } from 'lucide-react';
import axios from 'axios';
import { API } from '../App';

const Integrations = ({ user }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [integrations, setIntegrations] = useState([]);
  const [showWordPressModal, setShowWordPressModal] = useState(false);
  const [showWebhookModal, setShowWebhookModal] = useState(false);
  const [userPlan, setUserPlan] = useState('free');
  
  const [wpConfig, setWpConfig] = useState({
    url: '',
    username: '',
    app_password: ''
  });
  
  const [webhookConfig, setWebhookConfig] = useState({
    url: '',
    headers: {}
  });

  const [showDudaModal, setShowDudaModal] = useState(false);
  const [dudaConfig, setDudaConfig] = useState({
    api_username: '',
    api_password: '',
    site_name: ''
  });
  const [dudaConnected, setDudaConnected] = useState(false);

  useEffect(() => {
    if (!user) {
      navigate('/');
      return;
    }
    fetchIntegrations();
    fetchDudaStatus();
    fetchUserPlan();
  }, [user, navigate]);

  const fetchUserPlan = async () => {
    try {
      const response = await axios.get(`${API}/usage/${user.id}`);
      setUserPlan(response.data.plan || 'free');
    } catch (error) {
      console.error('Error fetching user plan:', error);
    }
  };

  const fetchIntegrations = async () => {
    try {
      const response = await axios.get(`${API}/integrations/${user.id}`);
      setIntegrations(response.data);
    } catch (error) {
      console.error('Error fetching integrations:', error);
    }
  };

  const handleSaveWordPress = async () => {
    setLoading(true);
    try {
      await axios.post(`${API}/integrations?user_id=${user.id}`, {
        platform: 'wordpress',
        config: wpConfig,
        auto_publish: false
      });
      await fetchIntegrations();
      setShowWordPressModal(false);
      alert('WordPress integration saved!');
    } catch (error) {
      console.error('Error saving WordPress integration:', error);
      alert('Error saving integration');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveWebhook = async () => {
    setLoading(true);
    try {
      await axios.post(`${API}/integrations?user_id=${user.id}`, {
        platform: 'webhook',
        config: webhookConfig,
        auto_publish: false
      });
      await fetchIntegrations();
      setShowWebhookModal(false);
      alert('Webhook integration saved!');
    } catch (error) {
      console.error('Error saving webhook integration:', error);
      alert('Error saving integration');
    } finally {
      setLoading(false);
    }
  };

  const fetchDudaStatus = async () => {
    try {
      // First check if user has a location ID saved
      const userResponse = await axios.get(`${API}/users/${user.id}`);
      const userLocationId = userResponse.data?.duda_location_id;
      
      if (userLocationId) {
        setDudaConnected(true);
        setDudaConfig({
          api_username: '',
          api_password: '',
          site_name: userLocationId
        });
        return;
      }
      
      // Fall back to checking global Duda status
      const response = await axios.get(`${API}/duda/status`);
      setDudaConnected(response.data.connected);
      if (response.data.connected) {
        setDudaConfig({
          api_username: response.data.api_username || '',
          api_password: '••••••••',
          site_name: response.data.site_name || ''
        });
      }
    } catch (error) {
      console.error('Error fetching Studio Sites status:', error);
    }
  };

  const handleSaveDuda = async () => {
    setLoading(true);
    try {
      await axios.post(`${API}/duda/configure`, {
        api_username: dudaConfig.api_username,
        api_password: dudaConfig.api_password,
        site_name: dudaConfig.site_name
      });
      await fetchDudaStatus();
      setShowDudaModal(false);
      alert('✅ Studio Sites integration saved successfully!');
    } catch (error) {
      console.error('Error saving Studio Sites integration:', error);
      alert('Error saving Studio Sites integration: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  // New simplified function for Location ID only (uses master API key)
  const handleSaveDudaLocation = async () => {
    setLoading(true);
    try {
      // Save the location ID to the user's record
      await axios.post(`${API}/user/${user.id}/duda-location`, {
        location_id: dudaConfig.site_name
      });
      setDudaConnected(true);
      setShowDudaModal(false);
      alert('✅ Studio Sites Location ID saved successfully!');
    } catch (error) {
      console.error('Error saving Location ID:', error);
      alert('Error saving Location ID: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  const handleTestDuda = async () => {
    setLoading(true);
    try {
      const response = await axios.post(`${API}/duda/test`);
      alert(`✅ Connection successful!\n\nSite: ${response.data.site_name}\nStatus: ${response.data.status}`);
    } catch (error) {
      alert('❌ Connection failed: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  const wordpressIntegration = integrations.find(i => i.platform === 'wordpress');
  const webhookIntegration = integrations.find(i => i.platform === 'webhook');

  // Check if user has access to features
  // Allow all plans to test integrations (Free, Starter, Pro)
  const hasAutoPost = true;
  const hasContentPlatforms = userPlan === 'pro' || userPlan === 'autopilot';

  if (!user) return null;

  return (
    <div className="min-h-screen bg-gray-50">
      <Helmet>
        <title>Integrations | QuillFire - Connect Your Platforms</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      {/* Header */}
      <nav className="bg-white border-b border-gray-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <Button onClick={() => navigate('/dashboard')} variant="ghost">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="flex items-center space-x-2">
              <LinkIcon className="w-6 h-6 text-purple-600" />
              <span className="text-xl font-bold text-gray-900">Integrations</span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className={`px-3 py-1 rounded-full text-sm font-medium ${
              userPlan === 'pro' ? 'bg-orange-100 text-orange-700' :
              userPlan === 'starter' ? 'bg-blue-100 text-blue-700' :
              'bg-gray-100 text-gray-700'
            }`}>
              {userPlan === 'pro' ? '👑 Pro' : userPlan === 'starter' ? 'Starter' : 'Free'} Plan
            </span>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <div className="max-w-5xl mx-auto px-6 py-12">
        
        {/* Website Platforms Section */}
        <div className="mb-12">
          <div className="mb-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-2">🌐 Website Platforms</h2>
            <p className="text-gray-600">
              Auto-post blogs directly to your website. {!hasAutoPost && <span className="text-orange-600 font-medium">Upgrade to Pro to enable auto-posting.</span>}
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            {/* WordPress */}
            <div className={`bg-white rounded-xl p-6 border-2 ${wordpressIntegration ? 'border-green-300' : 'border-gray-200'} hover:shadow-md transition-all`}>
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <span className="text-xl">📝</span>
                </div>
                <div className="flex-1">
                  <h3 className="font-bold text-gray-900">WordPress</h3>
                  {wordpressIntegration ? (
                    <span className="text-xs text-green-600 flex items-center gap-1">
                      <CheckCircle2 className="w-3 h-3" /> Connected
                    </span>
                  ) : (
                    <span className="text-xs text-gray-500">Not connected</span>
                  )}
                </div>
              </div>
              <p className="text-sm text-gray-600 mb-4">43% of all websites. Direct REST API integration.</p>
              <Button
                onClick={() => setShowWordPressModal(true)}
                size="sm"
                className={wordpressIntegration ? "bg-gray-100 text-gray-700 hover:bg-gray-200" : "bg-blue-600 hover:bg-blue-700 text-white"}
                disabled={!hasAutoPost}
              >
                {!hasAutoPost && <Lock className="w-3 h-3 mr-1" />}
                {wordpressIntegration ? 'Update' : 'Connect'}
              </Button>
            </div>

            {/* Wix */}
            <div className="bg-white rounded-xl p-6 border-2 border-gray-200 hover:shadow-md transition-all">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 bg-yellow-100 rounded-lg flex items-center justify-center">
                  <span className="text-xl">✨</span>
                </div>
                <div className="flex-1">
                  <h3 className="font-bold text-gray-900">Wix</h3>
                  <span className="text-xs text-gray-500">Not connected</span>
                </div>
              </div>
              <p className="text-sm text-gray-600 mb-4">Popular website builder for small businesses.</p>
              <Button size="sm" className="bg-yellow-500 hover:bg-yellow-600 text-white" disabled={!hasAutoPost}>
                {!hasAutoPost && <Lock className="w-3 h-3 mr-1" />}
                Connect
              </Button>
            </div>

            {/* Squarespace */}
            <div className="bg-white rounded-xl p-6 border-2 border-gray-200 hover:shadow-md transition-all">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 bg-gray-900 rounded-lg flex items-center justify-center">
                  <span className="text-xl text-white">■</span>
                </div>
                <div className="flex-1">
                  <h3 className="font-bold text-gray-900">Squarespace</h3>
                  <span className="text-xs text-gray-500">Not connected</span>
                </div>
              </div>
              <p className="text-sm text-gray-600 mb-4">Beautiful templates for creative businesses.</p>
              <Button size="sm" className="bg-gray-900 hover:bg-gray-800 text-white" disabled={!hasAutoPost}>
                {!hasAutoPost && <Lock className="w-3 h-3 mr-1" />}
                Connect
              </Button>
            </div>

            {/* Shopify */}
            <div className="bg-white rounded-xl p-6 border-2 border-gray-200 hover:shadow-md transition-all">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                  <span className="text-xl">🛒</span>
                </div>
                <div className="flex-1">
                  <h3 className="font-bold text-gray-900">Shopify</h3>
                  <span className="text-xs text-gray-500">Not connected</span>
                </div>
              </div>
              <p className="text-sm text-gray-600 mb-4">E-commerce platform with built-in blog.</p>
              <Button size="sm" className="bg-green-600 hover:bg-green-700 text-white" disabled={!hasAutoPost}>
                {!hasAutoPost && <Lock className="w-3 h-3 mr-1" />}
                Connect
              </Button>
            </div>

            {/* Webflow */}
            <div className="bg-white rounded-xl p-6 border-2 border-gray-200 hover:shadow-md transition-all">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <span className="text-xl">🌊</span>
                </div>
                <div className="flex-1">
                  <h3 className="font-bold text-gray-900">Webflow</h3>
                  <span className="text-xs text-gray-500">Not connected</span>
                </div>
              </div>
              <p className="text-sm text-gray-600 mb-4">Professional design platform for agencies.</p>
              <Button size="sm" className="bg-blue-600 hover:bg-blue-700 text-white" disabled={!hasAutoPost}>
                {!hasAutoPost && <Lock className="w-3 h-3 mr-1" />}
                Connect
              </Button>
            </div>

            {/* Studio Sites */}
            <div className={`bg-white rounded-xl p-6 border-2 ${dudaConnected ? 'border-green-300' : 'border-gray-200'} hover:shadow-md transition-all`}>
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                  <span className="text-xl">🌐</span>
                </div>
                <div className="flex-1">
                  <h3 className="font-bold text-gray-900">Studio Sites</h3>
                  {dudaConnected ? (
                    <span className="text-xs text-green-600 flex items-center gap-1">
                      <CheckCircle2 className="w-3 h-3" /> Connected
                    </span>
                  ) : (
                    <span className="text-xs text-gray-500">Not connected</span>
                  )}
                </div>
              </div>
              <p className="text-sm text-gray-600 mb-4">Agency-focused website builder with API.</p>
              <div className="flex gap-2">
                {dudaConnected && (
                  <Button onClick={handleTestDuda} size="sm" variant="outline" disabled={loading}>
                    Test
                  </Button>
                )}
                <Button
                  onClick={() => setShowDudaModal(true)}
                  size="sm"
                  className={dudaConnected ? "bg-gray-100 text-gray-700 hover:bg-gray-200" : "bg-orange-600 hover:bg-orange-700 text-white"}
                  disabled={!hasAutoPost}
                >
                  {!hasAutoPost && <Lock className="w-3 h-3 mr-1" />}
                  {dudaConnected ? 'Update' : 'Connect'}
                </Button>
              </div>
            </div>

            {/* Webhook */}
            <div className={`bg-white rounded-xl p-6 border-2 ${webhookIntegration ? 'border-green-300' : 'border-gray-200'} hover:shadow-md transition-all md:col-span-2`}>
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                  <Zap className="w-5 h-5 text-purple-600" />
                </div>
                <div className="flex-1">
                  <h3 className="font-bold text-gray-900">Custom Webhook</h3>
                  {webhookIntegration ? (
                    <span className="text-xs text-green-600 flex items-center gap-1">
                      <CheckCircle2 className="w-3 h-3" /> Connected
                    </span>
                  ) : (
                    <span className="text-xs text-gray-500">Not connected</span>
                  )}
                </div>
              </div>
              <p className="text-sm text-gray-600 mb-4">Universal integration via Make.com, Zapier, or custom API. Works with any platform.</p>
              <Button
                onClick={() => setShowWebhookModal(true)}
                size="sm"
                className={webhookIntegration ? "bg-gray-100 text-gray-700 hover:bg-gray-200" : "bg-purple-600 hover:bg-purple-700 text-white"}
                disabled={!hasAutoPost}
              >
                {!hasAutoPost && <Lock className="w-3 h-3 mr-1" />}
                {webhookIntegration ? 'Update' : 'Connect'}
              </Button>
            </div>
          </div>
        </div>

        {/* Content Platforms Section - Pro Only */}
        <div className="mb-12">
          <div className="mb-6">
            <div className="flex items-center gap-2 mb-2">
              <h2 className="text-2xl font-bold text-gray-900">📢 Content Platforms</h2>
              <span className="bg-orange-100 text-orange-700 px-2 py-0.5 rounded-full text-xs font-bold flex items-center gap-1">
                <Crown className="w-3 h-3" /> PRO
              </span>
            </div>
            <p className="text-gray-600">
              Expand your reach by posting to content platforms. {!hasContentPlatforms && <span className="text-orange-600 font-medium">Upgrade to Pro to unlock.</span>}
            </p>
          </div>

          <div className={`grid md:grid-cols-3 gap-4 ${!hasContentPlatforms ? 'opacity-60' : ''}`}>
            {/* Medium */}
            <div className="bg-white rounded-xl p-6 border-2 border-gray-200 hover:shadow-md transition-all">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                  <span className="text-xl">📗</span>
                </div>
                <div className="flex-1">
                  <h3 className="font-bold text-gray-900">Medium</h3>
                  <span className="text-xs text-amber-600">Coming Soon</span>
                </div>
              </div>
              <p className="text-sm text-gray-600 mb-4">Thought leadership & SEO backlinks.</p>
              <Button size="sm" variant="outline" disabled>
                {!hasContentPlatforms && <Lock className="w-3 h-3 mr-1" />}
                Coming Soon
              </Button>
            </div>

            {/* LinkedIn */}
            <div className="bg-white rounded-xl p-6 border-2 border-gray-200 hover:shadow-md transition-all">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <span className="text-xl">💼</span>
                </div>
                <div className="flex-1">
                  <h3 className="font-bold text-gray-900">LinkedIn Articles</h3>
                  <span className="text-xs text-amber-600">Coming Soon</span>
                </div>
              </div>
              <p className="text-sm text-gray-600 mb-4">B2B content marketing & authority.</p>
              <Button size="sm" variant="outline" disabled>
                {!hasContentPlatforms && <Lock className="w-3 h-3 mr-1" />}
                Coming Soon
              </Button>
            </div>

            {/* Google Business Profile */}
            <div className="bg-white rounded-xl p-6 border-2 border-gray-200 hover:shadow-md transition-all">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                  <span className="text-xl">📍</span>
                </div>
                <div className="flex-1">
                  <h3 className="font-bold text-gray-900">Google Business</h3>
                  <span className="text-xs text-amber-600">Coming Soon</span>
                </div>
              </div>
              <p className="text-sm text-gray-600 mb-4">Local SEO posts for your business listing.</p>
              <Button size="sm" variant="outline" disabled>
                {!hasContentPlatforms && <Lock className="w-3 h-3 mr-1" />}
                Coming Soon
              </Button>
            </div>
          </div>
        </div>

        {/* Social Media Section - Pro Only */}
        <div className="mb-12">
          <div className="mb-6">
            <div className="flex items-center gap-2 mb-2">
              <h2 className="text-2xl font-bold text-gray-900">📱 Social Media</h2>
              <span className="bg-orange-100 text-orange-700 px-2 py-0.5 rounded-full text-xs font-bold flex items-center gap-1">
                <Crown className="w-3 h-3" /> PRO
              </span>
            </div>
            <p className="text-gray-600">
              Social post generation & scheduling. {!hasContentPlatforms && <span className="text-orange-600 font-medium">Upgrade to Pro to unlock.</span>}
            </p>
          </div>

          <div className={`grid md:grid-cols-3 gap-4 ${!hasContentPlatforms ? 'opacity-60' : ''}`}>
            {/* Facebook */}
            <div className="bg-white rounded-xl p-6 border-2 border-gray-200">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <span className="text-xl">📘</span>
                </div>
                <div className="flex-1">
                  <h3 className="font-bold text-gray-900">Facebook</h3>
                  <span className="text-xs text-amber-600">Coming Soon</span>
                </div>
              </div>
              <p className="text-sm text-gray-600 mb-4">Post to your Facebook Pages.</p>
              <Button size="sm" variant="outline" disabled>
                {!hasContentPlatforms && <Lock className="w-3 h-3 mr-1" />}
                Coming Soon
              </Button>
            </div>

            {/* Instagram */}
            <div className="bg-white rounded-xl p-6 border-2 border-gray-200">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 bg-pink-100 rounded-lg flex items-center justify-center">
                  <span className="text-xl">📸</span>
                </div>
                <div className="flex-1">
                  <h3 className="font-bold text-gray-900">Instagram</h3>
                  <span className="text-xs text-amber-600">Coming Soon</span>
                </div>
              </div>
              <p className="text-sm text-gray-600 mb-4">Share visual content.</p>
              <Button size="sm" variant="outline" disabled>
                {!hasContentPlatforms && <Lock className="w-3 h-3 mr-1" />}
                Coming Soon
              </Button>
            </div>

            {/* X/Twitter */}
            <div className="bg-white rounded-xl p-6 border-2 border-gray-200">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 bg-gray-900 rounded-lg flex items-center justify-center">
                  <span className="text-xl text-white">𝕏</span>
                </div>
                <div className="flex-1">
                  <h3 className="font-bold text-gray-900">X / Twitter</h3>
                  <span className="text-xs text-amber-600">Coming Soon</span>
                </div>
              </div>
              <p className="text-sm text-gray-600 mb-4">Share updates and threads.</p>
              <Button size="sm" variant="outline" disabled>
                {!hasContentPlatforms && <Lock className="w-3 h-3 mr-1" />}
                Coming Soon
              </Button>
            </div>
          </div>
        </div>

        {/* Help Section */}
        <div className="bg-gradient-to-br from-orange-50 to-amber-50 rounded-2xl p-8 border-2 border-orange-200">
          <h3 className="text-xl font-bold text-gray-900 mb-4">📚 Need Help Connecting?</h3>
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <h4 className="font-semibold text-gray-900 mb-2">Quick Setup Guides</h4>
              <div className="space-y-2 text-gray-700 text-sm">
                <p>• <strong>WordPress:</strong> WP Admin → Users → Profile → Application Passwords</p>
                <p>• <strong>Studio Sites:</strong> Dashboard → Business Tools → API Access</p>
                <p>• <strong>Wix/Squarespace/Shopify:</strong> Coming soon - join waitlist</p>
                <p>• <strong>Custom:</strong> Use Webhook with Make.com or Zapier</p>
              </div>
            </div>
            <div>
              <h4 className="font-semibold text-gray-900 mb-2">Have Questions?</h4>
              <p className="text-gray-700 text-sm mb-3">Our team is here to help you get connected and publishing.</p>
              <Button 
                size="sm" 
                className="bg-orange-600 hover:bg-orange-700 text-white"
                onClick={() => window.open('mailto:support@quillfire.ai', '_blank')}
              >
                Contact Support
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* WordPress Modal */}
      {showWordPressModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-8 max-w-2xl w-full">
            <h3 className="text-2xl font-bold mb-6">Connect WordPress</h3>
            
            <div className="space-y-4 mb-6">
              <div>
                <Label htmlFor="wp-url">WordPress Site URL *</Label>
                <Input
                  id="wp-url"
                  type="url"
                  placeholder="https://yoursite.com"
                  value={wpConfig.url}
                  onChange={(e) => setWpConfig({...wpConfig, url: e.target.value})}
                  className="mt-1"
                />
              </div>
              
              <div>
                <Label htmlFor="wp-username">Username *</Label>
                <Input
                  id="wp-username"
                  placeholder="admin"
                  value={wpConfig.username}
                  onChange={(e) => setWpConfig({...wpConfig, username: e.target.value})}
                  className="mt-1"
                />
              </div>
              
              <div>
                <Label htmlFor="wp-password">Application Password *</Label>
                <Input
                  id="wp-password"
                  type="password"
                  placeholder="xxxx xxxx xxxx xxxx xxxx xxxx"
                  value={wpConfig.app_password}
                  onChange={(e) => setWpConfig({...wpConfig, app_password: e.target.value})}
                  className="mt-1"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Generate in WP Admin → Users → Your Profile → Application Passwords
                </p>
              </div>
            </div>

            <div className="flex justify-end space-x-3">
              <Button variant="outline" onClick={() => setShowWordPressModal(false)}>
                Cancel
              </Button>
              <Button
                onClick={handleSaveWordPress}
                disabled={loading || !wpConfig.url || !wpConfig.username || !wpConfig.app_password}
                className="bg-blue-600 hover:bg-blue-700 text-white"
              >
                {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                Save Connection
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Webhook Modal */}
      {showWebhookModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-8 max-w-2xl w-full">
            <h3 className="text-2xl font-bold mb-6">Connect via Webhook</h3>
            
            <div className="space-y-4 mb-6">
              <div>
                <Label htmlFor="webhook-url">Webhook URL *</Label>
                <Input
                  id="webhook-url"
                  type="url"
                  placeholder="https://hooks.make.com/..."
                  value={webhookConfig.url}
                  onChange={(e) => setWebhookConfig({...webhookConfig, url: e.target.value})}
                  className="mt-1"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Use Make.com, Zapier, or your custom API endpoint
                </p>
              </div>
              
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-sm font-semibold text-gray-700 mb-2">Payload Format:</p>
                <pre className="text-xs bg-gray-900 text-green-400 p-3 rounded overflow-x-auto">
{`{
  "title": "Blog Title",
  "content": "Full content...",
  "meta_title": "SEO title",
  "meta_description": "SEO description",
  "slug": "url-slug"
}`}
                </pre>
              </div>
            </div>

            <div className="flex justify-end space-x-3">
              <Button variant="outline" onClick={() => setShowWebhookModal(false)}>
                Cancel
              </Button>
              <Button
                onClick={handleSaveWebhook}
                disabled={loading || !webhookConfig.url}
                className="bg-purple-600 hover:bg-purple-700 text-white"
              >
                {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                Save Connection
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Studio Sites Modal */}
      {showDudaModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-8 max-w-2xl w-full">
            <h3 className="text-2xl font-bold mb-6">Connect Studio Sites</h3>
            
            <div className="space-y-4 mb-6">
              <div className="bg-green-50 rounded-lg p-4 border border-green-200 mb-4">
                <p className="text-sm font-semibold text-green-800 mb-1">✓ API Already Configured</p>
                <p className="text-sm text-green-700">Your agency&apos;s master API is ready. Just enter your Location ID below.</p>
              </div>
              
              <div>
                <Label htmlFor="duda-site">Location ID *</Label>
                <Input
                  id="duda-site"
                  placeholder="6f1021d3"
                  value={dudaConfig.site_name}
                  onChange={(e) => setDudaConfig({...dudaConfig, site_name: e.target.value})}
                  className="mt-1"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Found in your site URL: editor.duda.co/home/site/<strong>your-location-id</strong>
                </p>
              </div>

              <div className="bg-orange-50 rounded-lg p-4 border border-orange-200">
                <p className="text-sm font-semibold text-orange-800 mb-2">📍 Where to find your Location ID:</p>
                <ol className="text-sm text-orange-700 space-y-1 list-decimal list-inside">
                  <li>Your agency admin should have provided this to you</li>
                  <li>Or find it in your site URL after &quot;/site/&quot;</li>
                  <li>Example: editor.duda.co/home/site/<strong>6f1021d3</strong></li>
                </ol>
              </div>
            </div>

            <div className="flex justify-end space-x-3">
              <Button variant="outline" onClick={() => setShowDudaModal(false)}>
                Cancel
              </Button>
              <Button
                onClick={handleSaveDudaLocation}
                disabled={loading || !dudaConfig.site_name}
                className="bg-orange-600 hover:bg-orange-700 text-white"
              >
                {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                Save Location ID
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Integrations;
